import {
  collection,
  query,
  where,
  getDocs,
  orderBy,
  limit,
  startAfter,
  QueryDocumentSnapshot,
  DocumentData,
  Timestamp,
  enableNetwork,
  disableNetwork,
  getFirestore,
  connectFirestoreEmulator,
  Firestore,
  CollectionReference,
  Query,
  QuerySnapshot,
  DocumentSnapshot,
  writeBatch,
  runTransaction,
  onSnapshot,
  Unsubscribe
} from 'firebase/firestore';
import { db } from '../config/firebase';

// إعدادات التحسين المتقدمة
const ENHANCED_OPTIMIZATION_CONFIG = {
  BATCH_SIZE: 500, // زيادة حجم الدفعة
  CACHE_TTL: 10 * 60 * 1000, // 10 دقائق
  RETRY_ATTEMPTS: 5,
  RETRY_DELAY: 2000,
  MAX_QUERY_LIMIT: 2000,
  CONNECTION_TIMEOUT: 30000,
  QUERY_TIMEOUT: 60000,
  CACHE_MAX_SIZE: 1000,
  BATCH_DELAY: 100,
  CONCURRENT_QUERIES: 5
};

// ذاكرة تخزين مؤقت متقدمة مع LRU
class LRUCache<T> {
  private cache = new Map<string, { data: T; timestamp: number; accessCount: number }>();
  private maxSize: number;

  constructor(maxSize: number = ENHANCED_OPTIMIZATION_CONFIG.CACHE_MAX_SIZE) {
    this.maxSize = maxSize;
  }

  get(key: string): T | null {
    const item = this.cache.get(key);
    if (item) {
      item.accessCount++;
      item.timestamp = Date.now();
      return item.data;
    }
    return null;
  }

  set(key: string, data: T): void {
    if (this.cache.size >= this.maxSize) {
      // إزالة العنصر الأقل استخداماً
      let oldestKey = '';
      let oldestTime = Date.now();
      let lowestAccess = Infinity;

      for (const [k, v] of this.cache.entries()) {
        if (v.accessCount < lowestAccess || 
            (v.accessCount === lowestAccess && v.timestamp < oldestTime)) {
          oldestKey = k;
          oldestTime = v.timestamp;
          lowestAccess = v.accessCount;
        }
      }
      this.cache.delete(oldestKey);
    }

    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      accessCount: 1
    });
  }

  clear(): void {
    this.cache.clear();
  }

  size(): number {
    return this.cache.size;
  }

  keys(): string[] {
    return Array.from(this.cache.keys());
  }
}

// مدير الاتصالات المتقدم
class ConnectionManager {
  private static instance: ConnectionManager;
  private isOnline = true;
  private connectionRetries = 0;
  private maxRetries = 3;

  static getInstance(): ConnectionManager {
    if (!ConnectionManager.instance) {
      ConnectionManager.instance = new ConnectionManager();
    }
    return ConnectionManager.instance;
  }

  async ensureConnection(): Promise<void> {
    if (!this.isOnline) {
      try {
        await enableNetwork(db);
        this.isOnline = true;
        this.connectionRetries = 0;
        console.log('🌐 Firebase connection restored');
      } catch (error) {
        console.error('Failed to restore Firebase connection:', error);
        throw error;
      }
    }
  }

  async handleConnectionError(error: any): Promise<void> {
    if (error.code === 'unavailable' || error.code === 'deadline-exceeded') {
      this.isOnline = false;
      this.connectionRetries++;
      
      if (this.connectionRetries <= this.maxRetries) {
        console.log(`🔄 Attempting to reconnect (${this.connectionRetries}/${this.maxRetries})`);
        await new Promise(resolve => setTimeout(resolve, 2000));
        await this.ensureConnection();
      } else {
        throw new Error('فشل في الاتصال بـ Firebase بعد عدة محاولات');
      }
    }
  }
}

// مدير الاستعلامات المتقدم
class QueryManager {
  private static instance: QueryManager;
  private activeQueries = new Map<string, Promise<any>>();
  private queryQueue: Array<() => Promise<any>> = [];
  private processingQueue = false;

  static getInstance(): QueryManager {
    if (!QueryManager.instance) {
      QueryManager.instance = new QueryManager();
    }
    return QueryManager.instance;
  }

  async executeQuery<T>(
    queryKey: string,
    queryFn: () => Promise<T>,
    useCache: boolean = true
  ): Promise<T> {
    // التحقق من الاستعلامات النشطة
    if (this.activeQueries.has(queryKey)) {
      console.log(`🔄 Reusing active query: ${queryKey}`);
      return this.activeQueries.get(queryKey) as Promise<T>;
    }

    // إضافة الاستعلام إلى الطابور إذا كان ممتلئاً
    if (this.activeQueries.size >= ENHANCED_OPTIMIZATION_CONFIG.CONCURRENT_QUERIES) {
      return new Promise((resolve, reject) => {
        this.queryQueue.push(async () => {
          try {
            const result = await this.executeQuery(queryKey, queryFn, useCache);
            resolve(result);
          } catch (error) {
            reject(error);
          }
        });
      });
    }

    // تنفيذ الاستعلام
    const queryPromise = this.executeQueryWithRetry(queryFn);
    this.activeQueries.set(queryKey, queryPromise);

    try {
      const result = await queryPromise;
      return result;
    } finally {
      this.activeQueries.delete(queryKey);
      this.processQueue();
    }
  }

  private async executeQueryWithRetry<T>(queryFn: () => Promise<T>): Promise<T> {
    const connectionManager = ConnectionManager.getInstance();
    let lastError: any;

    for (let attempt = 1; attempt <= ENHANCED_OPTIMIZATION_CONFIG.RETRY_ATTEMPTS; attempt++) {
      try {
        await connectionManager.ensureConnection();
        
        const timeoutPromise = new Promise<never>((_, reject) => {
          setTimeout(() => reject(new Error('Query timeout')), ENHANCED_OPTIMIZATION_CONFIG.QUERY_TIMEOUT);
        });

        const result = await Promise.race([queryFn(), timeoutPromise]);
        return result;
      } catch (error: any) {
        lastError = error;
        console.error(`Query attempt ${attempt} failed:`, error);

        if (attempt < ENHANCED_OPTIMIZATION_CONFIG.RETRY_ATTEMPTS) {
          await connectionManager.handleConnectionError(error);
          await new Promise(resolve => 
            setTimeout(resolve, ENHANCED_OPTIMIZATION_CONFIG.RETRY_DELAY * attempt)
          );
        }
      }
    }

    throw lastError;
  }

  private async processQueue(): Promise<void> {
    if (this.processingQueue || this.queryQueue.length === 0) return;

    this.processingQueue = true;
    while (this.queryQueue.length > 0 && 
           this.activeQueries.size < ENHANCED_OPTIMIZATION_CONFIG.CONCURRENT_QUERIES) {
      const nextQuery = this.queryQueue.shift();
      if (nextQuery) {
        nextQuery();
      }
    }
    this.processingQueue = false;
  }

  getActiveQueriesCount(): number {
    return this.activeQueries.size;
  }

  getQueueLength(): number {
    return this.queryQueue.length;
  }
}

// الخدمة المحسنة الرئيسية
export class EnhancedFirebaseService {
  private static instance: EnhancedFirebaseService;
  private cache: LRUCache<any>;
  private queryManager: QueryManager;
  private connectionManager: ConnectionManager;

  private constructor() {
    this.cache = new LRUCache();
    this.queryManager = QueryManager.getInstance();
    this.connectionManager = ConnectionManager.getInstance();
  }

  static getInstance(): EnhancedFirebaseService {
    if (!EnhancedFirebaseService.instance) {
      EnhancedFirebaseService.instance = new EnhancedFirebaseService();
    }
    return EnhancedFirebaseService.instance;
  }

  /**
   * استعلام محسن مع ذاكرة مؤقتة ومراقبة الأداء
   */
  async optimizedQuery<T>(
    collectionName: string,
    constraints: Array<{ field: string; operator: string; value: any }>,
    orderByField?: string,
    orderDirection: 'asc' | 'desc' = 'desc',
    limitCount?: number,
    cacheKey?: string
  ): Promise<T[]> {
    const queryKey = `${collectionName}_${JSON.stringify(constraints)}_${orderByField}_${orderDirection}_${limitCount}`;
    
    // التحقق من الذاكرة المؤقتة
    if (cacheKey) {
      const cached = this.cache.get(cacheKey);
      if (cached) {
        console.log(`📊 Using cached data for: ${cacheKey}`);
        return cached;
      }
    }

    return this.queryManager.executeQuery(queryKey, async () => {
      console.log(`🔍 Executing optimized query: ${queryKey}`);
      const startTime = Date.now();

      const collectionRef = collection(db, collectionName);
      let q: Query<DocumentData> = collectionRef;

      // تطبيق القيود
      constraints.forEach(({ field, operator, value }) => {
        q = query(q, where(field, operator as any, value));
      });

      // تطبيق الترتيب
      if (orderByField) {
        q = query(q, orderBy(orderByField, orderDirection));
      }

      // تطبيق الحد
      if (limitCount) {
        q = query(q, limit(limitCount));
      }

      const snapshot = await getDocs(q);
      const results = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as T[];

      const endTime = Date.now();
      console.log(`✅ Query completed in ${endTime - startTime}ms, returned ${results.length} documents`);

      // حفظ في الذاكرة المؤقتة
      if (cacheKey) {
        this.cache.set(cacheKey, results);
      }

      return results;
    });
  }

  /**
   * استعلام مجمع للبيانات المتعددة
   */
  async batchQuery<T>(queries: Array<{
    collectionName: string;
    constraints: Array<{ field: string; operator: string; value: any }>;
    orderByField?: string;
    orderDirection?: 'asc' | 'desc';
    limitCount?: number;
    cacheKey?: string;
  }>): Promise<T[][]> {
    console.log(`🔄 Executing batch query with ${queries.length} queries`);
    const startTime = Date.now();

    const promises = queries.map((queryConfig, index) => 
      this.optimizedQuery<T>(
        queryConfig.collectionName,
        queryConfig.constraints,
        queryConfig.orderByField,
        queryConfig.orderDirection,
        queryConfig.limitCount,
        queryConfig.cacheKey
      )
    );

    const results = await Promise.all(promises);
    const endTime = Date.now();
    
    console.log(`✅ Batch query completed in ${endTime - startTime}ms`);
    return results;
  }

  /**
   * استعلام الصيانة المحسن
   */
  async getOptimizedMaintenance(
    startDate: Date,
    endDate: Date,
    branchId?: string,
    status?: string
  ): Promise<any[]> {
    const startTimestamp = Timestamp.fromDate(startDate);
    const endTimestamp = Timestamp.fromDate(endDate);

    const constraints: { field: string; operator: string; value: any }[] = [
      { field: 'receivedAt', operator: '>=', value: startTimestamp },
      { field: 'receivedAt', operator: '<=', value: endTimestamp }
    ];

    if (branchId) {
      constraints.push({ field: 'branchId', operator: '==', value: String(branchId) });
    }

    if (status) {
      constraints.push({ field: 'status', operator: '==', value: String(status) });
    }

    const cacheKey = `maintenance_${startDate.toISOString()}_${endDate.toISOString()}_${branchId || 'all'}_${status || 'all'}`;

    return this.optimizedQuery(
      'maintenance',
      constraints,
      'receivedAt',
      'desc',
      ENHANCED_OPTIMIZATION_CONFIG.MAX_QUERY_LIMIT,
      cacheKey
    );
  }

  /**
   * استعلام المبيعات المحسن
   */
  async getOptimizedSales(
    startDate: Date,
    endDate: Date,
    branchId?: string
  ): Promise<any[]> {
    const startTimestamp = Timestamp.fromDate(startDate);
    const endTimestamp = Timestamp.fromDate(endDate);

    const constraints: { field: string; operator: string; value: any }[] = [
      { field: 'createdAt', operator: '>=', value: startTimestamp },
      { field: 'createdAt', operator: '<=', value: endTimestamp }
    ];

    if (branchId) {
      constraints.push({ field: 'branchId', operator: '==', value: String(branchId) });
    }

    const cacheKey = `sales_${startDate.toISOString()}_${endDate.toISOString()}_${branchId || 'all'}`;

    return this.optimizedQuery(
      'sales',
      constraints,
      'createdAt',
      'desc',
      ENHANCED_OPTIMIZATION_CONFIG.MAX_QUERY_LIMIT,
      cacheKey
    );
  }

  /**
   * استعلام المخزون المحسن
   */
  async getOptimizedInventory(
    showInStore?: boolean,
    categoryId?: string
  ): Promise<any[]> {
    const constraints: Array<{ field: string; operator: string; value: any }> = [];

    if (showInStore !== undefined) {
      constraints.push({ field: 'showInStore', operator: '==', value: showInStore });
    }

    if (categoryId) {
      constraints.push({ field: 'categoryId', operator: '==', value: categoryId });
    }

    const cacheKey = `inventory_${showInStore}_${categoryId || 'all'}`;

    return this.optimizedQuery(
      'inventory',
      constraints,
      'createdAt',
      'desc',
      ENHANCED_OPTIMIZATION_CONFIG.MAX_QUERY_LIMIT,
      cacheKey
    );
  }

  /**
   * مسح الذاكرة المؤقتة
   */
  clearCache(): void {
    this.cache.clear();
    console.log('🧹 Cache cleared');
  }

  /**
   * الحصول على إحصائيات الأداء
   */
  getPerformanceStats(): {
    cacheSize: number;
    activeQueries: number;
    queueLength: number;
    cacheKeys: string[];
  } {
    return {
      cacheSize: this.cache.size(),
      activeQueries: this.queryManager.getActiveQueriesCount(),
      queueLength: this.queryManager.getQueueLength(),
      cacheKeys: this.cache.keys()
    };
  }

  /**
   * مراقبة الأداء في الوقت الفعلي
   */
  startPerformanceMonitoring(): Unsubscribe {
    const interval = setInterval(() => {
      const stats = this.getPerformanceStats();
      console.log('📊 Performance Stats:', stats);
    }, 30000); // كل 30 ثانية

    return () => {
      clearInterval(interval);
    };
  }
}

// تصدير المثيل الافتراضي
export const enhancedFirebaseService = EnhancedFirebaseService.getInstance(); 